import { BookOpen, BookMarked } from 'lucide-react';
import type { AllocatedClass } from '@/features/teacher/types/teacher';

interface MyClassesCardProps {
  classes: AllocatedClass[];
  /**
   * Periods each section takes with this teacher this week, keyed on the
   * section's uuid. From the timetable, so a section not on it yet is absent
   * from the map and says so rather than being given a number.
   */
  periodsBySection?: Record<string, number>;
  onSelectClass?: (classId: string) => void;
  onViewAll?: () => void;
  /** Every allotment rather than the first four — the timetable page's full list. */
  showAll?: boolean;
  title?: string;
  /** The allotment the page is currently showing, marked in the list. */
  selectedClassId?: string;
}

export function MyClassesCard({
  classes,
  periodsBySection = {},
  onSelectClass,
  onViewAll,
  showAll = false,
  title = 'My Classes',
  selectedClassId,
}: MyClassesCardProps) {
  const shown = showAll ? classes : classes.slice(0, 4);

  /**
   * Allotments per section. The period count is the section's, not a subject's —
   * the timetable totals what the section takes with this teacher — so where two
   * subjects share a section the figure is labelled as the section's rather
   * than split between them by a guess.
   */
  const allotmentsInSection = new Map<string, number>();
  for (const cls of classes) {
    allotmentsInSection.set(
      cls.classSectionUuid,
      (allotmentsInSection.get(cls.classSectionUuid) ?? 0) + 1,
    );
  }

  return (
    <div className="card overflow-hidden bg-surface border border-line rounded-[16px] shadow-2xs">
      {/* Header */}
      <div className="border-b border-line bg-surface px-5 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="text-amber-500">
            <BookMarked className="h-4.5 w-4.5" />
          </span>
          <h2 className="text-base font-bold text-ink tracking-tight">{title}</h2>
          {showAll && (
            <span className="pill">{classes.length}</span>
          )}
        </div>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="text-sm font-bold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 transition-colors"
          >
            View all
          </button>
        )}
      </div>

      {/* List */}
      <div className={showAll ? 'p-4 grid grid-cols-1 sm:grid-cols-2 gap-3' : 'p-4 flex flex-col gap-3'}>
        {classes.length === 0 ? (
          <div className="py-6 text-center text-xs text-ink-3 font-medium">
            No classes allocated yet.
          </div>
        ) : (
          shown.map((cls) => (
            <button
              type="button"
              key={cls.id}
              onClick={() => onSelectClass?.(cls.id)}
              aria-current={cls.id === selectedClassId ? 'true' : undefined}
              className={`group w-full text-left p-2 rounded-[12px] border bg-surface hover:bg-slate-50/70 dark:hover:bg-surface-2 hover:border-indigo-300 dark:hover:border-indigo-500/30 transition-all cursor-pointer flex items-center justify-between gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                cls.id === selectedClassId ? 'border-indigo-400 dark:border-indigo-500/60' : 'border-line'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 group-hover:scale-105 transition-transform">
                  <BookOpen className="h-5 w-5" />
                </span>
                <span className="flex flex-col">
                  <span className="block text-sm font-bold text-ink group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {cls.className}
                  </span>
                  <span className="block text-xs text-ink-2 font-medium">
                    {cls.subject ?? 'Class teacher — no subject'}
                  </span>
                  <span className="block text-2xs text-ink-3 font-medium">
                    {cls.totalStudents} {cls.totalStudents === 1 ? 'Student' : 'Students'}
                  </span>
                </span>
              </span>

              {/* What the office actually put against this teacher's name, and
                  what the timetable actually gives them. Neither is invented:
                  the pill used to number the rows P1, P2, P3 by their position
                  in this list, and the line under it promised eight periods to
                  every subject teacher in the product. */}
              <span className="text-right flex flex-col items-end gap-1">
                <span className="px-2 py-0.5 rounded-pill text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-400 dark:border-indigo-500/25">
                  {cls.role}
                </span>
                <span className="text-2xs text-ink-3 font-medium">
                  {periodsBySection[cls.classSectionUuid]
                    ? `${periodsBySection[cls.classSectionUuid]} ${periodsBySection[cls.classSectionUuid] === 1 ? 'period' : 'periods'} / week${(allotmentsInSection.get(cls.classSectionUuid) ?? 0) > 1 ? ' in this section' : ''}`
                    : 'Not on the timetable yet'}
                </span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
